/* eslint-disable no-magic-numbers */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { ConsolidatedForecastListItem } from '@futrli-sdk/consolidations';
import { ForecastListItem } from '@futrli-sdk/predict';
import { getCreateConsolidatedForecastRequestData } from '@futrli/predict/forecast/create-forecast/get-create-consolidated-forecast-request-data';
import { getCreateForecastRequestData } from '@futrli/predict/forecast/create-forecast/get-create-forecast-request-data';
import { ForecastPreviousActualsProps } from '@futrli/predict/forecast/create-forecast/components/form-elements/forecast-previous-actuals';
import { useForecastStartDateField } from '@futrli/predict/forecast/create-forecast/hooks/useForecastStartDateField';
import { ForecastTypeOption } from '@futrli/predict/forecast/create-forecast/types';
import {
  NewConsolidatedForecastDataSource,
  NewForecastDataSource
} from '@futrli/predict/forecast/dashboard/types';
import { WizardFormErrors } from '@futrli/ui-components/components/sidebar/wizard/types';

import { ForecastDurationProps } from '../../../components/form-elements/forecast-duration';
import { ForecastFormElementIds } from '../../../components/form-elements/types';
import { ForecastNameProps } from '../../../components/form-elements/forecast-name';
import { formatDateForForm, getForecastFormType } from '../../forecast-form-utils';
import { DuplicateForecastFormType } from '../types';

type DuplicateForecast = ConsolidatedForecastListItem | ForecastListItem;
type DuplicableForecastListItem = ForecastListItem & {
  kind: Exclude<ForecastListItem['kind'], 'live'>;
};

/**
 * Manages duplicate-forecast form state, variant-specific validation, and create payloads.
 *
 * @param forecast - Organisation or consolidated forecast being duplicated.
 * @param isConsolidation - Whether to use consolidated form rules and request data.
 * @returns Form values, field handlers, validation, and request builders for the duplicate sidebar.
 */
export const useDuplicateForecastForm = (forecast: DuplicateForecast | undefined, isConsolidation: boolean) => {
  const [duration, setDuration] = useState('');
  const [errors, setErrors] = useState<WizardFormErrors>({});
  const [formType, setFormType] = useState<DuplicateForecastFormType>('legacy');
  const [name, setName] = useState('');
  const [previousActuals, setPreviousActuals] = useState(false);

  const intl = useIntl();
  const hasBeenValidated = useRef(false);
  const { from, getStartDate, maxDate, minDate, onChangeFrom, setFrom, validateStartDate } =
    useForecastStartDateField({ setErrors });

  useEffect(() => {
    if (!forecast) {
      return;
    }

    const nextFormType = getForecastFormType(forecast, isConsolidation);
    const nextFrom = formatDateForForm(forecast.window_from || forecast.forecast_from_date);

    setDuration('');
    setErrors({});
    setFormType(nextFormType);
    setFrom(nextFormType === 'rolling' ? '' : nextFrom);
    setName(
      intl.formatMessage(
        {
          id: 'predict.forecasting.duplicate_forecast_name.copy_of',
          defaultMessage: '[Copy] {name}'
        },
        { name: forecast.name }
      )
    );
    setPreviousActuals(false);
    hasBeenValidated.current = false;
  }, [forecast, intl, isConsolidation, setFrom]);

  /** Validates only the fields displayed by the active duplicate-form variant. */
  const validate = useCallback(() => {
    const newErrors: WizardFormErrors = {};

    if (!name.trim().length) {
      newErrors.name = {
        id: ForecastFormElementIds.Name,
        message:
          formType === 'budget'
            ? intl.formatMessage({
              id: 'predict.create_forecast.validation.budget_name_is_required',
              defaultMessage: 'Budget name is required'
            })
            : intl.formatMessage({
              id: 'predict.create_forecast.validation.forecast_name_is_required',
              defaultMessage: 'Forecast name is required'
            })
      };
    }

    if (formType !== 'rolling') {
      const startDateError = validateStartDate();

      if (startDateError) {
        newErrors.from = startDateError;
      }
    }

    if ((formType === 'fixed' || formType === 'rolling') && !duration) {
      newErrors.duration = {
        id: ForecastFormElementIds.Duration,
        message: intl.formatMessage({
          id: 'predict.create_forecast.validation.please_select_an_option',
          defaultMessage: 'Please select an option'
        })
      };
    }

    setErrors(newErrors);
    hasBeenValidated.current = true;
    return Object.values(newErrors).length === 0;
  }, [duration, formType, intl, name, validateStartDate]);

  /** Revalidates form values after the user has attempted submission once. */
  const revalidate = useCallback(() => {
    if (hasBeenValidated.current) {
      validate();
    }
  }, [validate]);

  /** Stores the selected fixed-year or full-financial-year duration. */
  const onChangeDuration: ForecastDurationProps['onChange'] = useCallback((e: any) => {
    setDuration(e.target.value);
  }, []);

  /** Stores the duplicated forecast or budget name. */
  const onChangeName: ForecastNameProps['onChange'] = useCallback((e: any) => {
    setName(e.target.value);
  }, []);

  /** Stores whether a rolling duplicate should display previous actuals. */
  const onChangePreviousActuals: ForecastPreviousActualsProps['onChange'] = useCallback((e: any) => {
    setPreviousActuals(e.target.checked);
  }, []);

  /** Builds an organisation create payload with the source forecast ID. */
  const getRequestData = useCallback(() => {
    if (!forecast || isConsolidation) {
      return undefined;
    }

    const organisationForecast = forecast as DuplicableForecastListItem;

    return getCreateForecastRequestData({
      datasource: NewForecastDataSource.FromDuplicate,
      durationFixed: formType === 'fixed' ? duration : '',
      durationRolling: formType === 'rolling' ? duration : '',
      from: getStartDate(),
      kind: organisationForecast.kind,
      name,
      previousActuals,
      sourceForecast: organisationForecast.id,
      type:
        formType === 'rolling'
          ? ForecastTypeOption.Rolling
          : formType === 'fixed'
            ? ForecastTypeOption.Fixed
            : undefined
    });
  }, [duration, forecast, formType, getStartDate, isConsolidation, name, previousActuals]);

  /** Builds a consolidated create payload with the source consolidated forecast ID. */
  const getConsolidatedRequestData = useCallback(() => {
    if (!forecast || !isConsolidation) {
      return undefined;
    }

    const consolidatedForecast = forecast as ConsolidatedForecastListItem;

    return getCreateConsolidatedForecastRequestData({
      datasource: NewConsolidatedForecastDataSource.CREATE_FROM_EXISTING,
      duration,
      from: getStartDate(),
      kind: consolidatedForecast.kind,
      name,
      sourceForecast: consolidatedForecast.id,
      sourceSubEntityForecasts: {}
    });
  }, [duration, forecast, getStartDate, isConsolidation, name]);

  return {
    duration,
    errors,
    formType,
    from,
    maxDate,
    minDate,
    name,
    previousActuals,
    getConsolidatedRequestData,
    getRequestData,
    onChangeDuration,
    onChangeFrom,
    onChangeName,
    onChangePreviousActuals,
    revalidate,
    setDuration,
    setFrom,
    validate
  };
};
